import { EditOutlined, EuroOutlined, ExportOutlined } from "@ant-design/icons";
import { Button, Flex, Table, Tooltip, type TableColumnType } from "antd";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { IngredientReferencePriceModal } from "../../components/IngredientReferencePriceModal.tsx";
import { useCostDisplay } from "../../context/CostDisplayContext.tsx";
import type { IngredientFragment, RecipeDetailFragment } from "../../gql/generated/graphqlSchema.tsx";
import { useSaving } from "../../hooks/useSaving.tsx";
import { routes } from "../../routes.ts";
import { noWrap } from "../../styles/shared.styles.ts";
import { RecipeTab } from "./constants.ts";
import { PriceButtonSpacer } from "./PriceButtonSpacer.tsx";
import { useRecipe } from "./RecipeContext.tsx";
import { RecipeCostBreakdownModal } from "./RecipeCostBreakdownModal.tsx";
import { rowCost, totalCost, useCostFormatter } from "./recipeCost.ts";

type IngredientRow = RecipeDetailFragment["ingredients"][number];

type RecipeCostColumn = {
  readonly showPrices: boolean;
  readonly priceColumn: TableColumnType<IngredientRow>;
  readonly costTotalCell: (index: number) => ReactNode;
  readonly costModals: ReactNode;
};

/**
 * Owns the price UI shared by the view and edit ingredient tabs: the price
 * column, the cost cell for the Общо summary row, and the price/breakdown
 * modals with their state. The read-only variant (spacer instead of the
 * change-price button) follows RecipeContext's readOnly flag.
 */
export const useRecipeCostColumn = (): RecipeCostColumn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recipe, units, readOnly, refetch } = useRecipe();
  const { trackSaving } = useSaving();
  const { showPrices, vatMode } = useCostDisplay();
  const formatCost = useCostFormatter();
  const [priceIngredient, setPriceIngredient] = useState<Pick<
    IngredientFragment,
    "id" | "name" | "referencePrice"
  > | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const priceColumn: TableColumnType<IngredientRow> = {
    title: `${t("recipes.price")} (${vatMode === "with" ? t("recipes.withVat") : t("recipes.withoutVat")})`,
    key: "price",
    align: "center",
    render: (_, record) => (
      <Flex align="center" className={noWrap} gap="small" justify="flex-end">
        <span>{formatCost(rowCost(recipe.referenceCost, record.id, vatMode))}</span>
        {record.__typename === "IngredientRecipeIngredient" &&
          (readOnly ? (
            <PriceButtonSpacer />
          ) : (
            <Tooltip destroyOnHidden title={t("ingredients.price.changeButton")}>
              <Button icon={<EditOutlined />} onClick={() => setPriceIngredient(record.ingredient)} />
            </Tooltip>
          ))}
        {record.__typename === "SubRecipeRecipeIngredient" && (
          <Tooltip destroyOnHidden title={t("recipes.openSubRecipeIngredients")}>
            <Button
              icon={<ExportOutlined />}
              onClick={() =>
                void navigate({ pathname: routes.recipeDetail.to(record.recipe.id), hash: RecipeTab.Ingredients })
              }
            />
          </Tooltip>
        )}
      </Flex>
    ),
  };

  const costTotalCell = (index: number) => (
    <Table.Summary.Cell align="right" index={index}>
      <Flex align="center" className={noWrap} gap="small" justify="flex-end">
        <strong>{formatCost(totalCost(recipe.referenceCost, vatMode))}</strong>
        <Tooltip destroyOnHidden title={t("recipes.costBreakdown.tooltip")}>
          <Button icon={<EuroOutlined />} onClick={() => setBreakdownOpen(true)} />
        </Tooltip>
      </Flex>
    </Table.Summary.Cell>
  );

  const costModals = (
    <>
      <RecipeCostBreakdownModal
        onClose={() => setBreakdownOpen(false)}
        open={breakdownOpen}
        recipe={recipe}
        units={units}
      />
      {priceIngredient != null && (
        <IngredientReferencePriceModal
          ingredient={priceIngredient}
          onClose={() => setPriceIngredient(null)}
          onSaved={() => trackSaving(() => refetch())}
          open
          units={units}
        />
      )}
    </>
  );

  return { showPrices, priceColumn, costTotalCell, costModals };
};
